<script setup lang="ts">
import { NuxtLink } from "#components";
import { ref } from "vue";
import { useIsProService } from "~/helpers/functions/IsProService";

import { scrollToId } from "~/helpers/functions/smoothScrool";
import { UseIsProfessional } from "~/states/animation";
const isProService = useIsProService();
const { toggleProfessional } = useCustom();

const router = useRouter();

const ringoverURL = `${import.meta.env.VITE_RINGOVER_LINK}`;
const clientPortalURL = `${import.meta.env.VITE_CLIENT_PORTAL_URL}`;
// const linkCreditUrl = `${import.meta.env.VITE_OLD_BASE_URL}/le-credit`;
const isHomepage = computed(() => router.currentRoute.value.path === "/");
const professional = UseIsProfessional();
// const isSinistrePage = computed(
//   () => router.currentRoute.value.path === "/sinistre"
// );
const isMenuOpen = ref(false);
const hasMenuOpened = ref(false);

/**
 * Toggle the full-screen menu.
 *
 * If the menu is currently open, close it; if it's closed, open it.
 */
const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
  hasMenuOpened.value = true;
};

const navigateToServices = async (isProfessional: boolean) => {
  professional.value = isProfessional;
  toggleProfessional(isProfessional);

  // If mobile version
  if (isMenuOpen.value) {
    isMenuOpen.value = false;
  }
  if (router.currentRoute.value.path !== "/") {
    await router.push("/");
  }
  nextTick(() => {
    scrollToId("#services");
  });
};

const redirectToCustomerPortal = () => {
  window.location.href = clientPortalURL;
};

const redirectToRingover = () => {
  window.location.href = ringoverURL;
};

watch(isMenuOpen, (newVal) => {
  if (newVal) {
    document.body.style.overflow = "hidden"; // Disable scrolling
  } else {
    document.body.style.overflow = ""; // Enable scrolling
  }
});
</script>

<template>
  <header :class="{ 'pro-service': isProService }">
    <nav class="navbar">
      <!-- Logo on the left -->
      <div class="navbar-logo">
        <NuxtLink to="/">
          <img
            v-if="!isProService"
            src="@/assets/img/logo.svg"
            class="mobile-logo"
            :alt="$t('header.fullscreenMenu.logoAlt')"
          />
          <img
            v-if="isProService"
            src="@/assets/img/blue-mobile-logo.svg"
            class="mobile-logo"
            :alt="$t('header.fullscreenMenu.logoAlt')"
          />
          <img
            v-if="isHomepage && !isProService"
            src="@/assets/img/white-logo.svg"
            :alt="$t('header.logoAlt')"
            class="desktop-logo"
          />
          <img
            v-else-if="isProService"
            src="@/assets/img/blue-logo.svg"
            :alt="$t('header.logoAlt')"
            class="desktop-logo"
          />

          <img
            v-else
            src="@/assets/img/purple-logo.svg"
            :alt="$t('header.logoAlt')"
            class="desktop-logo"
          />
        </NuxtLink>
      </div>

      <!-- Hamburger button -->
      <div class="hamburger" @click="toggleMenu">
        <div
          :class="[
            { active: isMenuOpen },
            { close: hasMenuOpened && !isMenuOpen },
          ]"
        >
          <img
            v-if="isHomepage"
            src="@/assets/img/hamburger.svg"
            :alt="$t('header.hamburgerAlt')"
          />
          <img
            v-if="!isHomepage && !isProService"
            src="@/assets/img/purple-hamburger.svg"
            :alt="$t('header.hamburgerAlt')"
          />
          <img
            v-if="isProService"
            src="@/assets/img/blue-hamburger.svg"
            :alt="$t('header.hamburgerAlt')"
          />
        </div>
      </div>

      <!-- Navbar items on the right (visible on larger screens) -->
      <ul
        v-show="!isMenuOpen"
        class="navbar-items"
        :class="[{ notHomepage: !isHomepage }]"
      >
        <li
          class="navbar-item soundEffect"
          @click.prevent="navigateToServices(false)"
        >
          <a class="heavy-text" href="#">{{
            $t("header.navbar.personalInsurance")
          }}</a>
        </li>
        <li
          class="navbar-item soundEffect"
          @click.prevent="navigateToServices(true)"
        >
          <a class="heavy-text" href="#">{{
            $t("header.navbar.professionalInsurance")
          }}</a>
        </li>
        <li class="navbar-item soundEffect">
          <NuxtLink class="heavy-text" to="/credit">{{
            $t("header.navbar.yourCredit")
          }}</NuxtLink>
        </li>
        <li class="navbar-item soundEffect">
          <NuxtLink class="heavy-text" to="/blog">{{
            $t("header.navbar.blog")
          }}</NuxtLink>
        </li>
        <li class="navbar-icons">
          <div>
            <a :href="ringoverURL" class="soundEffect"
              ><img
                v-if="!isProService"
                class="default"
                src="@/assets/img/custom/phone.svg"
                :alt="$t('alt.phone_icon')" />

              <img
                v-if="isProService"
                class="default"
                src="@/assets/img/custom/blue-phone.svg"
                :alt="$t('alt.phone_icon')" />
              <img
                class="hover"
                src="@/assets/img/custom/hover-phone.svg"
                :alt="$t('alt.phone_icon')"
            /></a>
            <img
              v-if="!isProService"
              class="arrow"
              src="@/assets/img/custom/arrow.png"
              :alt="$t('alt.arrow')"
            />
            <img
              v-if="isProService"
              src="@/assets/img/custom/blue-arrow.png"
              class="arrow"
              :alt="$t('alt.arrow')"
            />
            <a :href="clientPortalURL"
              ><img
                v-if="!isProService"
                src="@/assets/img/custom/account.svg"
                class="default"
                :alt="$t('alt.account_icon')" />
              <img
                v-if="isProService"
                src="@/assets/img/custom/blue-account.svg"
                class="default"
                :alt="$t('alt.account_icon')" />
              <img
                src="@/assets/img/custom/hover-account.svg"
                class="hover"
                :alt="$t('alt.account_icon')"
            /></a>
          </div>
        </li>
      </ul>
    </nav>

    <!-- Full-screen menu (visible on smaller screens) -->
    <div
      class="fullscreen-menu"
      :class="[
        { activeMobile: isMenuOpen },
        { close: hasMenuOpened && !isMenuOpen },
      ]"
    >
      <div class="fullscreen-menu-header">
        <img
          v-if="!isProService"
          src="@/assets/img/logo.svg"
          class="fullscreen-menu-logo"
          :alt="$t('header.fullscreenMenu.logoAlt')"
        />
        <img
          v-if="isProService"
          src="@/assets/img/blue-mobile-logo.svg"
          class="fullscreen-menu-logo"
          :alt="$t('header.fullscreenMenu.logoAlt')"
        />

        <button class="close-button" @click="toggleMenu">
          <img
            v-if="!isProService"
            src="@/assets/img/custom/purple-close.svg"
            :alt="$t('alt.close_icon')"
          />
          <img
            v-if="isProService"
            src="@/assets/img/custom/blue-close.svg"
            :alt="$t('alt.close_icon')"
          />
        </button>
      </div>
      <ul class="fullscreen-menu-items" @click.stop>
        <li class="fullscreen-menu-item" @click="navigateToServices(false)">
          <NuxtLink class="heavy-text" to="/">{{
            $t("header.navbar.personalInsurance")
          }}</NuxtLink>
        </li>
        <li class="fullscreen-menu-item" @click="navigateToServices(true)">
          <NuxtLink class="heavy-text" to="/">{{
            $t("header.navbar.professionalInsurance")
          }}</NuxtLink>
        </li>
        <li class="fullscreen-menu-item">
          <NuxtLink class="heavy-text" to="/credit" @click="toggleMenu">{{
            $t("header.navbar.yourCredit")
          }}</NuxtLink>
        </li>
        <li class="fullscreen-menu-item">
          <NuxtLink class="heavy-text" to="/blog" @click="toggleMenu">{{
            $t("header.navbar.blog")
          }}</NuxtLink>
        </li>
        <li class="acount-button">
          <button @click="redirectToCustomerPortal">
            <span>
              {{ $t("header.navbar.account") }}
            </span>
            <img
              src="@/assets/img/custom/white-account.svg"
              :alt="$t('alt.account_icon')"
            />
          </button>
        </li>
        <li class="contact-button">
          <button @click="redirectToRingover">
            <span>
              {{ $t("header.navbar.contact") }}
            </span>
            <img
              v-if="!isProService"
              src="@/assets/img/custom/phone.svg"
              :alt="$t('alt.phone_icon')"
            />
            <img
              v-if="isProService"
              src="@/assets/img/custom/blue-phone.svg"
              :alt="$t('alt.phone_icon')"
            />
          </button>
        </li>
      </ul>
    </div>
  </header>
</template>

<style scoped>
.desktop-logo {
  transition: filter 0.3s ease-in-out; /* Ajoute une animation fluide pour l'ombre */
}

.desktop-logo:hover {
  filter: drop-shadow(0 0 5px white); /* Ombre blanche douce */
}
.mobile-logo {
  display: none;
}
.navbar {
  position: relative;
  z-index: 2;
  background-color: transparent;
  display: flex;
  align-items: center;
  margin-top: 0.8rem;
  justify-content: space-between;
  padding: 1.5rem 3.8rem;
  color: var(--vt-c-white);
}

.navbar-logo img {
  height: 3vw;
  max-height: 57px;
  min-height: 30px;
  width: auto;
}

.navbar-items {
  list-style: none;
  display: flex;
  align-items: center;
  /* gap: 1.5rem; */
  margin: 0;
  padding: 0;
}
.navbar-items a,
.fullscreen-menu-item a {
  color: var(--vt-c-purple);
  text-decoration: none;
  text-align: left;
  font-size: clamp(0.8rem, 1.04vw, 28px);
  line-height: 28px;
  letter-spacing: 0;
  opacity: 1;
  transition: all 0.3s ease-in-out;
  padding: 1rem 1.5rem;
  border-radius: 50px;
  transition: all 0.3s ease;
  white-space: nowrap;
}
.pro-service .navbar-items a,
.pro-service .fullscreen-menu-item a {
  color: var(--vt-c-blue);
}

.navbar-items .navbar-item {
  padding: 0.75rem 0;
  border-radius: 50px;
  transition: all 0.3s ease;
}

.navbar-items .navbar-item:hover a {
  background: rgba(255, 255, 255, 0.15) 0 0 no-repeat;
  color: white;
  white-space: nowrap;
  text-decoration: none;
  text-align: left;
  letter-spacing: 0;
  opacity: 1;
}
.notHomepage .navbar-item:hover a {
  background: rgba(68, 28, 208, 0.15) 0 0 no-repeat padding-box;

  color: #441cd0;
}

.fullscreen-menu-item {
  margin-bottom: 1rem;
}

.hamburger {
  display: none;
}

.hamburger img {
  width: 2.5rem;
  margin: 3px 0;

  transition: 0.3s;
}
.close-button {
  background: none;
  border: none;
  color: #6d4be7;
  font-size: 2rem;
}

.fullscreen-menu {
  display: flex;
  justify-content: space-between;
  position: fixed;
  top: 0;
  left: 100%;
  width: 100%;
  height: 100vh;

  background: #e0d9f2 0 0 no-repeat;
  flex-direction: column;
  z-index: 22;
}
.pro-service .fullscreen-menu {
  background: #d0d3fa 0 0 no-repeat;
}
.activeMobile {
  animation: openMenu 0.5s ease-in-out;
  left: 0;
}
.close {
  animation: closeMenu 0.5s ease-in-out;
}

@keyframes openMenu {
  0% {
    left: 100%;
  }
  100% {
    left: 0;
  }
}
@keyframes closeMenu {
  0% {
    left: 0;
  }
  100% {
    left: 100%;
  }
}
.fullscreen-menu-items {
  list-style: none;
  padding: 0;
  text-align: center;
  margin: auto;
}
.fullscreen-menu-logo {
  width: 40px;
}

.fullscreen-menu-header {
  display: flex;
  /* position: absolute; */
  top: 0;
  /* width: 100%; */
  margin-top: 0.8rem;
  padding: 1rem 2rem;

  justify-content: space-between;
  align-items: center;
}

.navbar-icons div {
  display: flex;
  align-items: center;

  justify-content: center;
  gap: 0.5rem;
}

.navbar-icons div a {
  width: 60px;
  padding: unset;
  padding: 0.5rem;
  box-sizing: border-box;
  height: 60px;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
}
.hover {
  display: none;
}
.navbar-icons div a:hover .default {
  display: none;
}
.navbar-icons div a:hover .hover {
  display: block;
}
.navbar-icons div a:hover {
  background: rgba(255, 255, 255, 0.15) 0 0 no-repeat;
}

.navbar-icons div a:hover img {
  filter: brightness(0) invert(1);
}
.notHomepage .navbar-icons div a:hover img {
  filter: unset;
}
.notHomepage .navbar-icons div a:hover {
  background: rgba(68, 28, 208, 0.15) 0 0 no-repeat padding-box;
}
.navbar-icons div img {
  /* height: 50%; */
  max-height: 32px;
  transition: filter 0.3s ease;
}
.acount-button,
.contact-button {
  display: flex;
  justify-content: center;
}

.acount-button button,
.contact-button button {
  display: flex;
  /* margin-top: 1rem; */
  align-items: center;
  justify-content: space-between;
  padding: 10px 12px;
  width: 292px;
  height: 56px;
  border-radius: 50px;
  box-shadow: 0 0 20px var(--vt-c-purple);
  border: 1px solid var(--vt-c-purple);
}
.acount-button button span,
.contact-button button span {
  margin: auto;
}
.acount-button button {
  background-color: var(--vt-c-purple);
  color: var(--vt-c-white);
  margin-top: 1.5rem;
  margin-bottom: 1.5rem;
}

.contact-button button {
  color: var(--vt-c-purple);
}
.acount-button img,
.contact-button img {
  /* margin-left: auto;  */
  width: 2.5rem;

  height: 2.5rem;
}
.pro-service .acount-button button {
  background-color: var(--vt-c-blue);
}
.pro-service .contact-button button {
  color: var(--vt-c-blue);
}
.pro-service .contact-button button,
.pro-service .acount-button button {
  border-color: var(--vt-c-blue);

  box-shadow: 0 0 20px var(--vt-c-blue);
}
/* .acount-button img {
  width: 30px;
  height: 2.5rem;
} */

.navbar-icons div img:hover {
  filter: brightness(0) invert(1);

  box-sizing: border-box;
}
.arrow:hover {
  filter: unset;
  cursor: unset;
}
@media screen and (max-width: 1200px) {
  .navbar-items {
    gap: 0;
  }
  .navbar-items a {
    /* font-size: 0.7rem; */
    padding: 0.5rem 0.5rem;
  }
  .navbar-items .navbar-item {
    padding: 0.5rem 0;
  }
  .navbar {
    padding: 1rem 2rem;
  }
}

@media (max-width: 900px) {
  .navbar-items .navbar-item {
    padding: 0.5rem 0.2rem;
  }
  .desktop-logo {
    display: none;
  }
  .mobile-logo {
    display: block;
    height: 40px !important;
  }
  .navbar-items {
    display: none;
  }

  .hamburger {
    display: block;
  }
}
</style>
